import { useEffect, useRef, useState } from "react";
import { canListen, LiveListener } from "../audio/liveListen.ts";
import { useServices } from "../app/services.ts";
import { label } from "../data/display.ts";
import { useStore } from "../data/store.ts";
import { Icon } from "../ui/Icon.tsx";
import styles from "./Composer.module.css";

/** Seconds a live recording shows without a new frame, as in the app. */
const TTL_MS = 12_000;

/** Others recording in this chat right now; "Mitlesen" asks them for the live transcript. */
export function LiveRecordings({ conversationId }: { conversationId: string }) {
  const services = useServices();
  const [, tick] = useState(0);
  const { recordings, conversation } = useStore((s) => ({ recordings: [...(s.live.get(conversationId)?.values() ?? [])], conversation: s.conversations.get(conversationId) }));
  useEffect(() => {
    const timer = setInterval(() => tick((n) => n + 1), 2000);
    return () => clearInterval(timer);
  }, []);
  const shown = recordings.filter((r) => Date.now() - r.at < TTL_MS);
  const listener = useRef<LiveListener | null>(null);
  const [listening, setListening] = useState<string | null>(null);
  const stopListening = () => {
    listener.current?.stop();
    listener.current = null;
    setListening(null);
  };
  // Stops when that recording ends or the chat changes.
  useEffect(() => {
    if (listening && !shown.some((r) => r.user === listening)) stopListening();
  });
  useEffect(() => stopListening, [conversationId]);
  if (shown.length === 0) return null;
  return (
    <>
      {shown.map((r) => {
        const name = label(conversation?.members.find((m) => m.user.id === r.user)?.user).split(" ")[0];
        const turns = [...r.turns.entries()].sort((a, b) => a[0] - b[0]);
        return (
          <div key={r.user} className={styles.live}>
            <div className={styles.liveHead}>
              <Icon name="mic" size={16} /> {name} {r.state === "paused" ? "pausiert" : "nimmt auf …"}
              <span className={styles.liveButtons}>
                {r.audio && canListen() && (
                  <button
                    className={listening === r.user ? `${styles.readSmall} ${styles.readOn}` : styles.readSmall}
                    data-decoded-ms={listening === r.user ? Math.round(listener.current?.decodedMs ?? 0) : undefined}
                    onClick={() => {
                      if (listening === r.user) return stopListening();
                      stopListening();
                      listener.current = new LiveListener(services.realtime, conversationId, r.user);
                      setListening(r.user);
                    }}
                  >
                    {listening === r.user ? "Aufhören" : "Mithören"}
                  </button>
                )}
                {!r.text && (
                  <button className={styles.readSmall} onClick={() => services.realtime.send({ t: "read", conversation: conversationId, user: r.user })}>
                    Mitlesen
                  </button>
                )}
              </span>
            </div>
            {turns.length > 0 && (
              <p>
                {turns.map(([o, t]) => (
                  <span key={o} className={t.final ? undefined : styles.partial}>
                    {t.text}{" "}
                  </span>
                ))}
              </p>
            )}
          </div>
        );
      })}
    </>
  );
}
